import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import { GetRewardsV2Document, EnterpriseLoyaltySettingsQuery } from 'src/gql/graphql';
import { useGetDutchiePosLoyaltyConfig } from 'src/components/loyalty/use-get-dutchie-pos-loyalty-config';

export type UseLoyaltyStatusParams = {
  dispensaryId?: string;
  enterpriseLoyaltySettings: EnterpriseLoyaltySettingsQuery['enterpriseLoyaltySettings'];
  isoRegionCode: string | null | undefined;
  isUserReady: boolean;
  optInRequired: boolean;
  userPhone: string;
};

export type UseLoyaltyStatusReturn = {
  hasLoyaltyProgram: boolean;
  isLoading: boolean;
  isLoyaltyMember: boolean;
  showLoyaltyEnrollment: boolean;
};

/**
 * Checks loyalty program status for the user.
 * Supports enterprise path (enterpriseLoyaltySettings + isoRegionCode) and
 * dispensary path (dispensaryId → getDutchiePosLoyaltyConfig + getRewardsV2 by dispensaryId).
 */
export function useLoyaltyStatus({
  dispensaryId,
  enterpriseLoyaltySettings,
  isoRegionCode,
  isUserReady,
  optInRequired,
  userPhone,
}: UseLoyaltyStatusParams): UseLoyaltyStatusReturn {
  const isDispensaryPath = !!dispensaryId;
  const enterpriseId = enterpriseLoyaltySettings?.enterpriseId;

  // Dispensary path: fetch loyalty config to determine if dispensary has loyalty
  const { data: dispensaryLoyaltyConfig, loading: dispensaryConfigLoading } = useGetDutchiePosLoyaltyConfig({
    dispensaryId,
    skip: !isDispensaryPath,
  });

  const hasLoyaltyProgram = useMemo(() => {
    if (isDispensaryPath) {
      return dispensaryLoyaltyConfig?.isEnabled ?? false;
    }
    if (!isoRegionCode) {
      return false;
    }
    return enterpriseLoyaltySettings?.dposLoyaltyIsoRegionCodes?.includes(isoRegionCode) ?? false;
  }, [
    isDispensaryPath,
    dispensaryLoyaltyConfig?.isEnabled,
    enterpriseLoyaltySettings?.dposLoyaltyIsoRegionCodes,
    isoRegionCode,
  ]);

  const skipStatusCheck = isDispensaryPath
    ? !isUserReady || !hasLoyaltyProgram || !userPhone || !dispensaryId
    : !isUserReady || !hasLoyaltyProgram || !userPhone || !enterpriseId || !isoRegionCode;

  const { data: rewardsData, loading: rewardsLoading } = useQuery(GetRewardsV2Document, {
    skip: skipStatusCheck,
    variables: isDispensaryPath
      ? { phoneNumber: userPhone, dispensaryId }
      : { phoneNumber: userPhone, enterpriseId, isoRegionCode },
  });

  const isLoyaltyMember = rewardsData?.getRewardsV2?.isLoyaltyMember ?? false;

  const isLoading =
    (isDispensaryPath && dispensaryConfigLoading) || (!skipStatusCheck && (rewardsLoading || !rewardsData));
  const showLoyaltyEnrollment = !isLoading && hasLoyaltyProgram && optInRequired && !isLoyaltyMember;

  return {
    hasLoyaltyProgram,
    isLoading,
    isLoyaltyMember,
    showLoyaltyEnrollment,
  };
}
